import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import logoImage from '../../img/logo.png';
import {
  activateSalesContract,
  createSalesContractCheckout,
  getSalesContract,
  updateSalesContractCustomer,
} from '../../services/salesContractApi';
import '../../styles/SalesContractPage.css';

const money = (value) => new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL',
}).format(Number(value || 0));

const SalesContractPage = () => {
  const { uuid } = useParams();
  const [contract, setContract] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState({
    customer_name: '', customer_email: '', customer_document: '', customer_phone: '',
    billing_address: { line_1: '', zip_code: '', city: '', state: '' },
  });
  const [activation, setActivation] = useState({
    entidade_nome: '', entidade_cnpj: '', user_name: '', user_email: '',
    user_cpf: '', password: '', password_confirmation: '', saldo_inicial: '',
    saldo_data_corte: new Date().toISOString().split('T')[0],
  });
  const [activated, setActivated] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await getSalesContract(uuid);
      setContract(data);
      setCustomer((current) => ({
        ...current,
        customer_name: current.customer_name || data.customer_name || '',
        customer_email: current.customer_email || data.customer_email || '',
        customer_phone: current.customer_phone || data.customer_phone || '',
      }));
      setActivation((current) => ({
        ...current,
        user_name: current.user_name || data.customer_name || '',
        user_email: current.user_email || data.customer_email || '',
      }));
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Não foi possível carregar esta contratação.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [uuid]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!contract || contract.ready_for_activation || contract.activated) return undefined;
    const timer = window.setInterval(() => load(true), 10000);
    return () => window.clearInterval(timer);
  }, [contract, load]);

  const updateAddress = (field, value) => setCustomer((current) => ({
    ...current,
    billing_address: { ...current.billing_address, [field]: value },
  }));

  const saveCustomer = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await updateSalesContractCustomer(uuid, customer);
      setContract(data);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Confira os dados informados.');
    } finally {
      setLoading(false);
    }
  };

  const goToCheckout = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await createSalesContractCheckout(uuid, 'subscription');
      if (data.url) window.open(data.url, '_blank', 'noopener,noreferrer');
      else await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Não foi possível abrir o pagamento.');
    } finally {
      setLoading(false);
    }
  };

  const activate = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      await activateSalesContract(uuid, activation);
      setActivated(true);
    } catch (requestError) {
      const validation = requestError.response?.data?.errors;
      setError(validation ? Object.values(validation).flat()[0] : requestError.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !contract) return <main className="sales-contract sales-contract--center">Carregando contratação...</main>;
  if (error && !contract) return <main className="sales-contract sales-contract--center"><p>{error}</p><Link to="/inicio">Voltar ao site</Link></main>;

  return (
    <div className="sales-contract">
      <header><Link to="/inicio"><img src={logoImage} alt="Fluxo Clínica" /></Link></header>
      <main>
        <section className="sales-contract__summary">
          <span>Contratação segura</span>
          <h1>{contract.plan.name}</h1>
          <p>{contract.plan.description}</p>
          <div><small>Mensalidade</small><strong>{money(contract.monthly_price)} / mês</strong></div>
          <div><small>Implantação, cobrança única</small><strong>{money(contract.implementation_price)}</strong></div>
          <ul>{contract.plan.features.map((feature) => <li key={feature}>✓ {feature}</li>)}</ul>
        </section>

        <section className="sales-contract__card">
          {activated || contract.activated ? (
            <>
              <h2>Conta criada com sucesso</h2>
              <p>Use o e-mail e a senha cadastrados para entrar.</p>
              <Link className="sales-contract__button" to="/login">Entrar no Fluxo Clínica</Link>
            </>
          ) : !contract.has_customer_data ? (
            <form onSubmit={saveCustomer}>
              <h2>Dados de cobrança</h2>
              <p>Informe os dados do responsável pela contratação.</p>
              <input required placeholder="Nome ou razão social" value={customer.customer_name} onChange={(e) => setCustomer({ ...customer, customer_name: e.target.value })} />
              <input required type="email" placeholder="E-mail" value={customer.customer_email} onChange={(e) => setCustomer({ ...customer, customer_email: e.target.value })} />
              <input required placeholder="CPF ou CNPJ" value={customer.customer_document} onChange={(e) => setCustomer({ ...customer, customer_document: e.target.value })} />
              <input required placeholder="Telefone" value={customer.customer_phone} onChange={(e) => setCustomer({ ...customer, customer_phone: e.target.value })} />
              <input required placeholder="Endereço e número" value={customer.billing_address.line_1} onChange={(e) => updateAddress('line_1', e.target.value)} />
              <input required placeholder="CEP" value={customer.billing_address.zip_code} onChange={(e) => updateAddress('zip_code', e.target.value)} />
              <div className="sales-contract__row">
                <input required placeholder="Cidade" value={customer.billing_address.city} onChange={(e) => updateAddress('city', e.target.value)} />
                <input required maxLength={2} placeholder="UF" value={customer.billing_address.state} onChange={(e) => updateAddress('state', e.target.value.toUpperCase())} />
              </div>
              {error && <p className="sales-contract__error">{error}</p>}
              <button disabled={loading} className="sales-contract__action">{loading ? <><Spinner animation="border" size="sm" /> Salvando seus dados...</> : 'Continuar para o pagamento'}</button>
            </form>
          ) : contract.ready_for_activation ? (
            <form onSubmit={activate}>
              <h2>Crie sua conta</h2>
              <div className="sales-contract__activation-notice">
                <strong>Pagamento confirmado!</strong>
                <span>Agora cadastre o acesso e a clínica para começar a usar o Fluxo Clínica.</span>
              </div>
              <input required placeholder="Nome da clínica" value={activation.entidade_nome} onChange={(e) => setActivation({ ...activation, entidade_nome: e.target.value })} />
              <input required placeholder="CNPJ da clínica" value={activation.entidade_cnpj} onChange={(e) => setActivation({ ...activation, entidade_cnpj: e.target.value })} />
              <input required placeholder="Nome do administrador" value={activation.user_name} onChange={(e) => setActivation({ ...activation, user_name: e.target.value })} />
              <input required type="email" placeholder="E-mail de acesso" value={activation.user_email} onChange={(e) => setActivation({ ...activation, user_email: e.target.value })} />
              <input required placeholder="CPF do administrador" value={activation.user_cpf} onChange={(e) => setActivation({ ...activation, user_cpf: e.target.value })} />
              <div className="sales-contract__financial-fields">
                <div>
                  <label htmlFor="saldo-inicial">Saldo inicial em caixa e bancos</label>
                  <input id="saldo-inicial" type="number" min="0" step="0.01" inputMode="decimal" placeholder="0,00" value={activation.saldo_inicial} onChange={(e) => setActivation({ ...activation, saldo_inicial: e.target.value })} />
                  <small>Use o saldo disponível antes de começar a movimentar o sistema.</small>
                </div>
                <div>
                  <label htmlFor="saldo-data-corte">Data de apuração</label>
                  <input id="saldo-data-corte" required type="date" value={activation.saldo_data_corte} onChange={(e) => setActivation({ ...activation, saldo_data_corte: e.target.value })} />
                  <small>Data em que esse saldo foi conferido.</small>
                </div>
              </div>
              <input required type="password" placeholder="Senha" value={activation.password} onChange={(e) => setActivation({ ...activation, password: e.target.value })} />
              <input required type="password" placeholder="Confirme a senha" value={activation.password_confirmation} onChange={(e) => setActivation({ ...activation, password_confirmation: e.target.value })} />
              {error && <p className="sales-contract__error">{error}</p>}
              <button disabled={loading} className="sales-contract__action">{loading ? <><Spinner animation="border" size="sm" /> Criando sua clínica...</> : 'Criar clínica e acesso'}</button>
            </form>
          ) : (
            <>
              <h2>Conclua a contratação</h2>
              <div className="sales-contract__payment">
                <div className="sales-contract__charge-card sales-contract__charge-card--today">
                  <span>Hoje</span>
                  <strong>{money(Number(contract.monthly_price) + Number(contract.implementation_price))}</strong>
                  <small>Primeira mensalidade + implantação</small>
                  <div><span>Plano</span><b>{money(contract.monthly_price)}</b></div>
                  <div><span>Implantação</span><b>{money(contract.implementation_price)}</b></div>
                </div>
                <div className="sales-contract__charge-arrow">→</div>
                <div className="sales-contract__charge-card">
                  <span>Próximos meses</span>
                  <strong>{money(contract.monthly_price)}</strong>
                  <small>Somente a mensalidade do plano</small>
                  <div><span>Frequência</span><b>Mensal</b></div>
                </div>
              </div>
              <div className="sales-contract__checkout-action">
                {contract.subscription_status === 'active'
                  ? <b>Pagamento confirmado ✓</b>
                  : <button disabled={loading} onClick={goToCheckout} className="sales-contract__action">
                    {loading ? <><Spinner animation="border" size="sm" /> Abrindo ambiente seguro...</> : 'Pagar no ambiente seguro da Pagar.me'}
                  </button>}
              </div>
              <p className="sales-contract__note">Número, validade e CVV serão informados somente na página hospedada pela Pagar.me.</p>
              <p className="sales-contract__billing-notice"><strong>Atenção:</strong> a implantação é cobrada somente hoje. Mesmo que o checkout exiba o total na recorrência, as próximas cobranças serão de {money(contract.monthly_price)} por mês, conforme confirmação da Pagar.me.</p>
              {error && <p className="sales-contract__error">{error}</p>}
              <button className="sales-contract__refresh" disabled={loading} onClick={() => load()}>Atualizar confirmações</button>
            </>
          )}
        </section>
      </main>
    </div>
  );
};

export default SalesContractPage;